import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines } from "./Reveal";
import { ArrowRight } from "lucide-react";

const LOBBY =
  "https://images.unsplash.com/photo-1759038085950-1234ca8f5fed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwY29uY2llcmdlJTIwZGVza3xlbnwwfHx8fDE3ODUyOTkxMzl8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative grain overflow-hidden pt-[72px]"
    >
      <div className="beam-glow pointer-events-none absolute inset-0 z-0" />
      <div className="pointer-events-none absolute -top-24 right-[-8%] z-0 h-[460px] w-[460px] rounded-full bg-[var(--blush-deep)]/40 blur-3xl" />

      <div className="container-luxe relative z-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center py-20 lg:py-28 min-h-[calc(100vh-72px)]">
        {/* Copy */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="overline flex items-center gap-3"
            data-testid="hero-overline"
          >
            <span className="h-px w-8 bg-[var(--gold)]" />
            Reputation as Service
          </motion.p>

          <h1
            className="mt-7 serif font-medium leading-[0.98] tracking-tight text-[clamp(2.6rem,6vw,5rem)] text-foreground"
            data-testid="hero-heading"
          >
            <MaskLines
              lines={["The hospitality", "standard for your"]}
              delay={0.25}
            />
            <span className="block overflow-hidden">
              <motion.span
                className="block italic gold-shimmer"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.25 + 2 * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                digital storefront.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground"
          >
            We bring the rigor of five-star hospitality to your online reputation —
            turning everyday customer interactions into glowing reviews, higher
            search rankings, and sustainable revenue. Charlotte based, serving
            businesses anywhere in the U.S.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a
              href="#pricing"
              data-testid="hero-cta-tiers"
              className="group inline-flex items-center gap-2 bg-secondary px-7 py-3.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors duration-400"
            >
              View the Tiers
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 border border-[var(--gold)] px-7 py-3.5 text-sm text-foreground hover:bg-[var(--gold)]/10 transition-colors duration-400"
            >
              Book an Operations Audit Call
            </a>
          </motion.div>
        </div>

        {/* Parallax image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[420px] sm:h-[540px] lg:h-[620px]"
        >
          <div className="relative h-full w-full overflow-hidden">
            <motion.img
              src={LOBBY}
              alt="Luxury hotel lobby concierge desk"
              style={{ y: imgY, scale }}
              className="absolute inset-0 h-[118%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/10" />
          </div>
          {/* floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="absolute -bottom-6 -left-6 sm:left-[-32px] bg-background border border-border p-6 max-w-[220px] grain"
          >
            <p className="serif text-4xl text-foreground">5.0</p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[var(--gold)]">
              The Only Standard
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Reviews, reactivation & reporting, managed with white-glove care.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
